import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/sonner";
import { approveApplication, listApplications, rejectApplication, roleLabel } from "@/lib/api";

const detailLine = (a) =>
  [
    a.business_name && `Business: ${a.business_name}`,
    a.entity_type,
    a.occupation && `Occupation: ${a.occupation}`,
    a.operating_area && `Area: ${a.operating_area}`,
    a.existing_customer != null && `Existing customer: ${a.existing_customer ? "Yes" : "No"}`,
    a.referred_by_code && `Ref by: ${a.referred_by_code}`,
  ]
    .filter(Boolean)
    .join(" · ");

export const AdminApplications = () => {
  const query = useQuery({
    queryKey: ["applications", "pending"],
    queryFn: () => listApplications("pending"),
  });
  const [busy, setBusy] = useState(null);

  const act = async (id, fn, kind) => {
    setBusy(id);
    try {
      const data = await fn(id);
      if (kind === "approve") {
        toast.success(`Approved · ${data.referral_code}`);
        if (data.activation_link) {
          toast.info("Email not configured — activation link copied to console");
          // eslint-disable-next-line no-console
          console.log("Activation link:", data.activation_link);
        }
      } else {
        toast.success("Application rejected");
      }
      query.refetch();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Action failed");
    } finally {
      setBusy(null);
    }
  };

  const apps = query.data || [];

  return (
    <section className="premium-card table-card" data-testid="admin-applications">
      <div className="section-head">
        <div>
          <p className="eyebrow">MEMBERSHIP</p>
          <h2>Applications</h2>
          <p>Review and approve new member applications.</p>
        </div>
        {!query.isLoading && (
          <span className="rounded-full bg-brand-gold-soft px-3 py-1 text-[13px] font-bold text-brand-gold-deep">
            {apps.length} pending
          </span>
        )}
      </div>

      {query.isLoading ? (
        <div className="grid place-items-center py-10">
          <Loader2 className="animate-spin text-brand-muted" size={22} />
        </div>
      ) : apps.length ? (
        <div className="space-y-3">
          {apps.map((a) => {
            const details = detailLine(a);
            return (
              <div key={a.id} className="rounded-[14px] border border-black/5 bg-brand-surface p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <b className="text-[15px]">{a.first_name} {a.last_name}</b>
                      <span className="rounded-full bg-[#eaf2ef] px-2 py-0.5 text-[11px] font-bold text-brand">
                        {roleLabel(a.applying_as)}
                      </span>
                    </div>
                    <div className="mt-1 text-[13px] text-brand-muted">
                      {a.email} · {a.phone} · {[a.city, a.state].filter(Boolean).join(", ")}
                    </div>
                    {details && <div className="mt-1 text-[12px] text-brand-muted">{details}</div>}
                    {a.message && (
                      <div className="mt-1 text-[12px] italic text-brand-muted">“{a.message}”</div>
                    )}
                  </div>
                  <div className="flex shrink-0 gap-2">
                    <Button
                      size="sm"
                      disabled={busy === a.id}
                      onClick={() => act(a.id, approveApplication, "approve")}
                      className="rounded-[10px] bg-brand-forest text-white hover:bg-[#0d322d]"
                    >
                      <CheckCircle2 size={15} />Approve
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={busy === a.id}
                      onClick={() => act(a.id, rejectApplication, "reject")}
                      className="rounded-[10px]"
                    >
                      <XCircle size={15} />Reject
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="empty-inline"><span>No pending applications.</span></div>
      )}
    </section>
  );
};
